import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../../core";
import { ApiResponse, IFormat } from "../../../types";

type GetFormatsArg =
  | void
  | string
  | {
      formatId?: string;
      page?: number;
      limit?: number;
    };

export const formatApi = createApi({
  reducerPath: "formatApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Formats"],

  endpoints: (builder) => ({
    getFormats: builder.query<IFormat[], GetFormatsArg>({
      query: (arg) => {
        let formatId: string | undefined;
        let page: number | undefined;
        let limit: number | undefined;

        if (typeof arg === "string") {
          formatId = arg;
        } else if (typeof arg === "object" && arg !== null) {
          formatId = arg.formatId;
          page = arg.page;
          limit = arg.limit;
        }

        const baseUrl =
          formatId && formatId !== ""
            ? `${ApiEndpoints.getAllFormats}/${formatId}`
            : ApiEndpoints.format;

        const params = new URLSearchParams();
        if (page !== undefined) params.append("page", String(page));
        if (limit !== undefined) {
          params.append("limit", String(limit));
        } else {
          // Backwards-compatible default to fetch all when no limit is provided
          params.append("limit", "-1");
        }

        return {
          url: `${baseUrl}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (res: ApiResponse<IFormat[]>) => {
        return  res?.results ?? [];
      },
      providesTags: ["Formats"],
    }),

    getFormatById: builder.query<ApiResponse<IFormat>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.format}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Formats", id }],
    }),

    createFormat: builder.mutation<ApiResponse<IFormat>, Partial<IFormat>>({
      query: (data) => ({
        url: ApiEndpoints.createFormat,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Formats"],
    }),

    editFormat: builder.mutation<
      ApiResponse<IFormat>,
      { id: string; data: Partial<IFormat> }
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.editFormat}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "Formats",
        { type: "Formats", id },
      ],
    }),

    deleteFormat: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.deleteFormat}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Formats"],
    }),
  }),
});

export const {
  useGetFormatsQuery,
  useGetFormatByIdQuery,
  useCreateFormatMutation,
  useEditFormatMutation,
  useDeleteFormatMutation,
} = formatApi;
